import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import CssBaseline from '@material-ui/core/CssBaseline'
import TextField from '@material-ui/core/TextField'
import Link from '@material-ui/core/Link'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Container from '@material-ui/core/Container'
import UserService from '../../../services/User/index'
import { urls } from '../../../urls'
import { ServerError } from '../../../schemas/Error'

const EXPIRED_TOKEN_MESSAGE = 'El enlace para establecer la contraseña ha expirado.'
const INVALID_TOKEN_MESSAGE = 'El enlace para establecer la contraseña no es válido.'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: '#fafafa',
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  helpText: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
}))

const texts = {
  header: {
    title: 'Establecer contraseña',
    logo: {
      path: '/images/logotipo-crecer-dmc.svg',
      alt: 'logotipo de crecer dmc',
    },
  },
  field: {
    password: {
      name: 'password',
      label: 'Contraseña',
    },
    confirmPassword: {
      name: 'confirmPassword',
      label: 'Confirmar contraseña',
    },
    submitButton: {
      title: 'Guardar',
    },
  },
  missingToken: 'El enlace no es válido o está incompleto.',
  passwordRequired: 'La contraseña es requerida.',
  passwordsMismatch: 'Las contraseñas no coinciden.',
  success: 'Contraseña establecida correctamente',
  expiredHelp: 'Solicite un nuevo enlace al administrador o use Cambiar contraseña en el menú de su cuenta.',
  footer: {
    copyright: 'CRECER DMC',
  },
}

interface SetPasswordForm {
  password: string
  confirmPassword: string
}

const initState: SetPasswordForm = {
  password: '',
  confirmPassword: '',
}

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="https://material-ui.com/">
        {texts.footer.copyright}
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  )
}

/** Public page to set or change a password using a password_setup JWT from the link. */
export default function SetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const classes = useStyles()
  const [state, setState] = useState<SetPasswordForm>(initState)
  const [submitting, setSubmitting] = useState(false)
  const [tokenErrorHelp, setTokenErrorHelp] = useState('')

  const token = searchParams.get('token')?.trim() ?? ''
  const hasToken = token !== ''

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setState({ ...state, [name]: value })
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!hasToken) {
      return
    }

    if (state.password === '') {
      toast.error(texts.passwordRequired)
      return
    }

    if (state.password !== state.confirmPassword) {
      toast.error(texts.passwordsMismatch)
      return
    }

    setSubmitting(true)
    setTokenErrorHelp('')

    try {
      const userService = new UserService()
      await userService.setPassword(token, state.password)
      toast.success(texts.success)
      navigate(urls.page.login)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        const message = serverError?.response?.data?.message
        if (message) {
          toast.error(message)
          if (message === EXPIRED_TOKEN_MESSAGE || message === INVALID_TOKEN_MESSAGE) {
            setTokenErrorHelp(texts.expiredHelp)
          }
          return
        }
      }
      toast.error('No se pudo establecer la contraseña. Intente nuevamente.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <img
            src={texts.header.logo.path}
            alt={texts.header.logo.alt}
          />
        </Avatar>

        <Typography component="h1" variant="h5">
          {texts.header.title}
        </Typography>

        {!hasToken && (
          <Typography variant="body2" color="error" className={classes.helpText}>
            {texts.missingToken}
          </Typography>
        )}

        {tokenErrorHelp !== '' && (
          <Typography variant="body2" color="textSecondary" className={classes.helpText}>
            {tokenErrorHelp}
          </Typography>
        )}

        {hasToken && (
          <form className={classes.form} onSubmit={onSubmit} noValidate>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              onChange={handleChange}
              value={state.password}
              id={texts.field.password.name}
              label={texts.field.password.label}
              name={texts.field.password.name}
              type="password"
              autoFocus
              disabled={submitting}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              onChange={handleChange}
              value={state.confirmPassword}
              id={texts.field.confirmPassword.name}
              label={texts.field.confirmPassword.label}
              name={texts.field.confirmPassword.name}
              type="password"
              disabled={submitting}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={submitting}
            >
              {texts.field.submitButton.title}
            </Button>
          </form>
        )}
      </div>

      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  )
}
